import { FunctionComponent, SVGAttributes, useMemo } from 'react';
import * as z from 'zod';

import {
  checkNumberLength,
  checkValidityOfExpirationDate,
  checkValidityOfLuhn,
  checkValidityOfMaskedLast4,
  isNotEmpty,
} from '~/shared/lib/validation/validation';

import {
  BrandSchema,
  ProductSchema,
} from '../api/getCatalog/getCatalog.schema';
import { CatalogPriceResponseSchema } from '../api/getCatalogPrices/getCatalogPrices.schema';
import { fieldName } from '../constants/paymentCard';
import { useSteamSchema } from '../features/Steam/ui/SteamPage/SteamPage.scheme';
import { useLocalization } from '../localization/localization';
import { AmountSchema } from '../model/schemes/amount.scheme';

export type Amount = z.infer<typeof AmountSchema>;
export type AmountType = z.infer<typeof CatalogPriceResponseSchema>;

export const CertificateSchemaBase = z.object({
  name: z.string().optional(),
  icon: z.custom<FunctionComponent<SVGAttributes<SVGElement>>>().optional(),
  logo: z.custom<FunctionComponent<SVGAttributes<SVGElement>>>().optional(),
  color: z.string().optional(),
  brand: BrandSchema.optional(),
  products: z.array(ProductSchema).optional(),
  type: z.enum(['single', 'list']).optional(),
});

export type Certificate = z.infer<typeof CertificateSchemaBase>;

export const CardDataSchema = z.object({
  cardNumber: z.string().nonempty(),
  expirationDate: z.string().nonempty(),
  secureCode: z.string().nonempty(),
  cardholder: z.string().nonempty(),
  email: z.string().nonempty(),
});
export type CardData = z.infer<typeof CardDataSchema>;

export const FormSchemaBase = z.object({
  certificate: CertificateSchemaBase.optional(),
  amount: CatalogPriceResponseSchema.optional(),
  offer: z.boolean().optional(),
  cardData: CardDataSchema.optional(),
  ruData: z
    .object({
      [fieldName.EMAIL]: z.string().email(),
    })
    .optional(),
  product: ProductSchema.optional(),
  catalogPrices: CatalogPriceResponseSchema.optional(),
  steamData: z
    .object({
      login: z.string(),
      amount: z.number(),
    })
    .optional(),
  promocode: z.string().optional(),
});

export type FormType = z.infer<typeof FormSchemaBase>;

const EMAIL_REGEX =
  /^(?:[a-zA-Z0-9#$%&'+?^_`{|}~-]+(?:\.[a-zA-Z0-9#$%&'+?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-zA-Z0-9-]*[a-zA-Z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])$/;

export const useSchema = (isRu: boolean) => {
  const { t } = useLocalization();
  const { steamDataSchema } = useSteamSchema();

  const schema = useMemo(() => {
    const emailFieldSchema = z
      .string()
      .max(254, t('email validate'))
      .refine((email) => EMAIL_REGEX.test(email), t('Invalid email'));

    const base = {
      certificate: CertificateSchemaBase.refine(
        (cert) => cert.name && cert.name.trim().length > 0,
        { message: t('Choose service') },
      ),
      offer: z.boolean().optional(),
      product: ProductSchema.optional(),
      amount: CatalogPriceResponseSchema.optional(),
      steamData: z.custom<FormType['steamData']>().optional(),
      promocode: z
        .string()
        .max(256, t('promocode_validation_failed'))
        .regex(/^[a-zA-Z0-9]*$/, t('promocode_validation_failed'))
        .optional(),
    };

    const ruDataSchema = z.object({
      [fieldName.EMAIL]: emailFieldSchema.and(isNotEmpty()),
    });

    const cardDataSchema = z.object({
      [fieldName.CARD_NUMBER]: z.union([
        isNotEmpty()
          .and(
            checkNumberLength(
              16,
              t('Card number must contain N digits', {
                length: 16,
              }),
            ),
          )
          .and(checkValidityOfLuhn()),
        checkValidityOfMaskedLast4(),
      ]),

      [fieldName.EXPIRATION_DATE]: isNotEmpty().and(
        checkValidityOfExpirationDate(),
      ),
      [fieldName.SECURE_CODE]: isNotEmpty().and(
        checkNumberLength(
          3,
          t('Secure code must contain N digits', { length: 3 }),
        ),
      ),
      [fieldName.CARDHOLDER]: isNotEmpty(),
      [fieldName.EMAIL]: emailFieldSchema.and(isNotEmpty()),
    });

    const ruData = {
      ruData: z
        .object({
          [fieldName.EMAIL]: z.string().email().catch(''),
        })
        .optional()
        .catch(undefined),
    };

    const cardData = {
      cardData: z
        .object({
          [fieldName.CARD_NUMBER]: z.string().catch(''),
          [fieldName.EXPIRATION_DATE]: z.string().catch(''),
          [fieldName.SECURE_CODE]: z.string().catch(''),
          [fieldName.CARDHOLDER]: z.string().catch(''),
          [fieldName.EMAIL]: z.string().email().catch(''),
        })
        .optional()
        .catch(undefined),
    };

    return z
      .object({
        ...base,
        ...(isRu ? ruData : cardData),
      })
      .superRefine((data: any, ctx) => {
        const isSteam = data.certificate?.brand?.slug === 'steam';

        if (isSteam) {
          if (!data.offer) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['offer'],
              message: t('Accept offer'),
            });
          }

          if (!data.steamData) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['steamData'],
              message: t('Steam data required'),
            });
          } else {
            // Валидируем steamData с помощью steamDataSchema
            const result = steamDataSchema.safeParse(data.steamData);
            if (!result.success) {
              result.error.issues.forEach((issue) => {
                ctx.addIssue({
                  ...issue,
                  path: ['steamData', ...issue.path],
                });
              });
            }
          }
        } else {
          // Валидация для обычных сертификатов
          if (!data.offer) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['offer'],
              message: t('Accept offer'),
            });
          }

          if (!data.product) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['product'],
              message: t('Choose product'),
            });
          }

          if (!data.amount) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['amount'],
              message: t('Choose amount'),
            });
          }

          // Проверка ruData или cardData
          // cardData пока отсутствует в проекте
          if (isRu) {
            if (!data.ruData) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['ruData'],
                message: t('Email required'),
              });
            } else {
              const result = ruDataSchema.safeParse(data.ruData);
              if (!result.success) {
                result.error.issues.forEach((issue) => {
                  ctx.addIssue({
                    ...issue,
                    path: ['ruData', ...issue.path],
                  });
                });
              }
            }
          } else {
            if (!data.cardData) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['cardData'],
                message: t('Card data required'),
              });
            } else {
              const result = cardDataSchema.safeParse(data.cardData);
              if (!result.success) {
                result.error.issues.forEach((issue) => {
                  ctx.addIssue({
                    ...issue,
                    path: ['cardData', ...issue.path],
                  });
                });
              }
            }
          }
        }
      });
  }, [t, isRu, steamDataSchema]);

  return { schema };
};
